import md5 from "md5";

import { EditableType } from "hoxton-shared/enums/editables";
import { OverwriteScope } from "hoxton-shared/enums/overwrites";
import rgbToHex from "hoxton-shared/utils/rgbToHex";
import getMediaItemResizedUrl from "hoxton-shared/utils/getMediaItemResizedUrl";
import isMediaType, { isImageType } from "hoxton-shared/utils/isMediaType";
import type {
    Editable,
    EditableGroup,
    EditableWithOverwrites,
    HTML5MasterTemplate,
    MasterTemplate,
    OverwriteScopes
} from "hoxton-types";

import type { EditableOverwrite } from "@/types/types";
import { deepClone, getImageDimensionsFromUrl } from "@/utils/index";
import config from "@/config";

function getUrlWithoutQueryParams(url: string) {
    return url.split("?")[0];
}

export function getOverwriteScopeId(overwrite: EditableOverwrite) {
    switch (overwrite.scope) {
        /* these levels are not currently in use but leaving in case we need to reuse it
    case OverwriteScope.Campaign:
        return `${ overwrite.language }__${ overwrite.scope }`;

    case OverwriteScope.Template:
        return `${ overwrite.language }__${ overwrite.scope }__${ overwrite.masterTemplateId }`; */

        case OverwriteScope.EditableGroup:
            return `${overwrite.language}__${overwrite.scope}__${overwrite.editableGroupValueIds.sort().join("_")}`;

        case OverwriteScope.EditableGroupTemplate:
            return `${overwrite.language}__${overwrite.scope}__${overwrite.editableGroupValueIds.sort().join("_")}__${overwrite.masterTemplateId}`;

        default:
            return `${overwrite.language}__${overwrite.scope}`;
    }
}

function mergeOverwrites(baseOverwrites: EditableOverwrite[], extensionOverwrites: EditableOverwrite[]) {
    const existingOverwritesMap = baseOverwrites.reduce<Record<string, EditableOverwrite>>((acc, cur) => {
        acc[getOverwriteScopeId(cur)] = cur;
        return acc;
    }, {});

    const mergedOverwrites = extensionOverwrites.reduce((acc, newOverwrite) => {
        acc[getOverwriteScopeId(newOverwrite)] = newOverwrite;
        return acc;
    }, existingOverwritesMap);

    return Object.values(mergedOverwrites);
}

export function computeEditableValue(
    editable: Editable,
    masterTemplateId: string,
    overwriteValueObject: EditableOverwrite | null,
    masterTemplates: Record<string, MasterTemplate> = {}
) {
    const defaultValue = getDefaultValue(editable, masterTemplateId);

    if (!overwriteValueObject) {
        return defaultValue;
    }

    const overwriteValue = getEditableValue(editable, overwriteValueObject, masterTemplateId, masterTemplates);

    if (overwriteValue === null || overwriteValue === undefined) {
        return defaultValue;
    }

    return overwriteValue;
}

export function extractOverwriteValue(overwriteValue: EditableOverwrite) {
    if (overwriteValue.mediaItem === null && overwriteValue.value === undefined) {
        return false;
    }

    return overwriteValue;
}

export function findMissingFonts(editable: Editable, availableFontNames: string[]) {
    if (!editable.layers) {
        return [];
    }

    const missingFonts = editable.layers.reduce((acc, { value }) => {
        if (value?.text) {
            value.text.characterStyles?.forEach(({ fontName, fontAvailable }) => {
                if (!fontAvailable && fontName && !availableFontNames.includes(fontName)) {
                    acc.add(fontName);
                }
            });
        }

        return acc;
    }, new Set());

    return Array.from(missingFonts);
}

export function getDefaultValue(editable: Editable, masterTemplateId: string | null) {
    const defaultValues = editable.defaultValues;

    if (!Array.isArray(defaultValues)) {
        return null;
    }

    const templateDefaultValue = defaultValues.find(defaultValue => defaultValue.masterTemplateId === masterTemplateId);

    if (templateDefaultValue) {
        return templateDefaultValue.value;
    }

    return null;
}

export function getGroupPlaceholder(editable: Editable, masterTemplateIds: string[], defaultLabel = "") {
    if (editable.type !== EditableType.Text && editable.type !== EditableType.Textarea) {
        return defaultLabel;
    }

    const sample = getDefaultValue(editable, masterTemplateIds[0] ?? null);
    const sameDefaults = !masterTemplateIds.some(masterTemplateId => {
        const val = getDefaultValue(editable, masterTemplateId);
        return val !== sample;
    });

    return sameDefaults ? sample : defaultLabel;
}

export function getEditableValue(
    editable: Editable,
    overwriteValueObject: EditableOverwrite,
    templateId: string,
    masterTemplates: Record<string, MasterTemplate> = {}
) {
    if (isMediaType(editable.type)) {
        if (editable.type === EditableType.Folder && overwriteValueObject.mediaItem) {
            return `/${overwriteValueObject.mediaItem.persistentKey}/`;
        }

        if (overwriteValueObject.mediaItem?.resizeSettings) {
            return getMediaItemResizedUrl({
                editable,
                mediaItem: overwriteValueObject.mediaItem,
                masterTemplate: masterTemplates[templateId] as HTML5MasterTemplate,
                renderService:
                    (import.meta.env.RENDERER_SERVICE as string | undefined) ?? config.appConfig.RENDERER_SERVICE
            });
        }

        return overwriteValueObject.mediaItem?.url;
    }

    return overwriteValueObject.value;
}

export function getDefaultSettings(editable: Editable, templateId: string) {
    const layers = editable.layers ?? [];
    const templateDefaultLayer = layers.find(({ masterTemplateId }) => masterTemplateId === templateId);
    const hasLayer = templateDefaultLayer?.value;
    const isTextLayer = hasLayer && templateDefaultLayer.value?.type === "textLayer";

    const setVisibility = () => {
        if (!hasLayer) {
            return {};
        }

        // Set visibility
        return { visible: templateDefaultLayer.value?.visible !== false };
    };

    const setFontStyles = () => {
        if (!isTextLayer) {
            return {};
        }
        // For now we're only using the first styles object found
        const characterStyles = templateDefaultLayer.value?.text.characterStyles?.[0];
        const paragraphStyles = templateDefaultLayer.value?.text.paragraphStyles[0];

        // Return if not found
        if (!characterStyles) {
            return {};
        }

        if (!paragraphStyles) {
            return {};
        }

        // Extract values
        let hexFontColor = "";
        const { fontColor, fontSize, fontFamily } = characterStyles;
        // Convert default rgb to hex
        if (fontColor?.rgb) {
            const { red, green, blue } = fontColor.rgb;
            hexFontColor = rgbToHex(red, green, blue, true);
        }
        // extracting paragraph style
        const { alignment } = paragraphStyles;

        // Set default values
        return {
            ...(fontFamily && { fontFamily }),
            ...(fontSize && { fontSize }),
            ...(hexFontColor && { fontColor: hexFontColor }),
            ...(alignment && { textAlignment: alignment })
        };
    };

    const setPosition = () => {
        if (!hasLayer || !templateDefaultLayer.value?.bounds) {
            return {};
        }

        return deepClone(templateDefaultLayer.value.bounds);
    };

    return {
        ...setVisibility(),
        ...setFontStyles(),
        ...setPosition()
    };
}

export function getEditableSettings(overwriteValueObject: EditableOverwrite) {
    return overwriteValueObject.settings;
}

function getEditableOverwriteHash(editableOverwrite: Partial<EditableOverwrite>) {
    return md5(JSON.stringify(editableOverwrite));
}

export async function getImageDimensionsByDefaultValueFromEditablesWithOverwrites(
    editablesWithOverwritesByCampaign: EditableWithOverwrites[]
) {
    const imageDefaultValues = editablesWithOverwritesByCampaign.reduce<string[]>((acc, editablesWithOverwrites) => {
        const { type, defaultValues } = editablesWithOverwrites.editable;

        if (!isImageType(type)) {
            return acc;
        }

        return [...acc, ...defaultValues.map(({ value }) => value as string)];
    }, []);

    const imageDefaultValuesWithDimensions = await Promise.all(
        imageDefaultValues.map(async imageDefaultValue => {
            try {
                const { height, width } = await getImageDimensionsFromUrl(imageDefaultValue);

                return {
                    defaultValue: imageDefaultValue,
                    dimensions: { height, width }
                };
            } catch {
                return null;
            }
        })
    );

    const imageDimensionsByDefaultValue = imageDefaultValuesWithDimensions.reduce<
        Record<string, { width: number; height: number }>
    >((acc, imageDefaultValueWithDimensions) => {
        if (imageDefaultValueWithDimensions) {
            const { defaultValue, dimensions } = imageDefaultValueWithDimensions;
            /*
                    We use the URL without query params as the query params for an
                    image can change over time (specifically the access keys),
                    while a user is using the editor which would lead to images
                    appearing to not be resized due to dimensions not existing
                    for the image.
                */
            const url = getUrlWithoutQueryParams(defaultValue);

            if (!url) {
                return acc;
            }

            acc[url] = dimensions;
        }

        return acc;
    }, {});

    return imageDimensionsByDefaultValue;
}

export function getEditablesWithOverwritesByCampaignWithDefaultImageDimensions(
    editablesWithOverwritesByCampaign: EditableWithOverwrites[],
    imageDimensionsByDefaultValue: Record<string, { width: number; height: number }>
) {
    return editablesWithOverwritesByCampaign.map(editablesWithOverwrites => {
        if (!isImageType(editablesWithOverwrites.editable.type)) {
            return editablesWithOverwrites;
        }

        const defaultValuesWithImageDimensions = editablesWithOverwrites.editable.defaultValues.map(defaultValue => {
            if (defaultValue.dimensions) {
                return defaultValue;
            }

            if (!defaultValue.value) {
                return defaultValue;
            }

            const url = getUrlWithoutQueryParams(defaultValue.value as string);

            if (!url) {
                return defaultValue;
            }

            const dimensions = imageDimensionsByDefaultValue[url];

            if (dimensions) {
                return {
                    ...defaultValue,
                    dimensions
                };
            }

            return defaultValue;
        });
        return {
            ...editablesWithOverwrites,
            editable: {
                ...editablesWithOverwrites.editable,
                defaultValues: defaultValuesWithImageDimensions
            }
        };
    });
}

export function getEditableGroupsByCampaignWithDefaultImageDimensions(
    editableGroupsByCampaign: EditableGroup[],
    imageDimensionsByDefaultValue: Record<string, { width: number; height: number }>
) {
    return editableGroupsByCampaign.map(editableGroup => {
        const editablesWithDefaultImageDimenions = editableGroup.editables.map(editable => {
            if (!isImageType(editable.type)) {
                return editable;
            }

            const defaultValuesWithImageDimensions = editable.defaultValues.map(defaultValue => {
                if (defaultValue.dimensions) {
                    return defaultValue;
                }

                const url = getUrlWithoutQueryParams(defaultValue.value as string);

                if (!url) {
                    return defaultValue;
                }

                const dimensions = imageDimensionsByDefaultValue[url];

                if (dimensions) {
                    return {
                        ...defaultValue,
                        dimensions
                    };
                }

                return defaultValue;
            });
            return {
                ...editable,
                defaultValues: defaultValuesWithImageDimensions
            };
        });
        return {
            ...editableGroup,
            editables: editablesWithDefaultImageDimenions
        };
    });
}

export function mergeEditables(base: EditableWithOverwrites[], extension: EditableWithOverwrites[]) {
    const baseEditableMap = base.reduce<Record<string, EditableWithOverwrites>>((acc, cur) => {
        // We must make a new array of overwrites here so the merge does not end up in the base
        acc[cur.editable._id] = { ...cur, overwrites: [...cur.overwrites] };
        return acc;
    }, {});

    const merged = extension.reduce<Record<string, EditableWithOverwrites>>((acc, extensionEditable) => {
        if (!acc[extensionEditable.editable._id]) {
            acc[extensionEditable.editable._id] = {
                editable: extensionEditable.editable,
                overwrites: extensionEditable.overwrites
            };

            return acc;
        }

        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-expect-error
        acc[extensionEditable.editable._id].overwrites = mergeOverwrites(
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-expect-error
            acc[extensionEditable.editable._id].overwrites,
            extensionEditable.overwrites
        );

        return acc;
    }, baseEditableMap);

    return Object.values(merged);
}

export function setScopeValues(scope: OverwriteScopes, updatedEditable: Partial<EditableOverwrite>) {
    const scopedEditable: Partial<EditableOverwrite & { hash: string }> = deepClone(updatedEditable);
    // Remove properties not required based on scopre
    if (scope === OverwriteScope.Campaign) {
        delete scopedEditable.editableGroupValueIds;
        delete scopedEditable.masterTemplateId;
    } else if (scope === OverwriteScope.Template) {
        delete scopedEditable.editableGroupValueIds;
    } else if (scope === OverwriteScope.EditableGroup) {
        delete scopedEditable.masterTemplateId;
    }

    // Get hash of scope, must be done before added values otherwise it will always be a different hash
    scopedEditable.hash = getEditableOverwriteHash(scopedEditable);

    // Return scoped editable
    return scopedEditable;
}
